import React from 'react';
import { TextInput, Button, MultiSelect } from '../../../components/UI';
import TickerInput from '../../../components/TickerInput';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setFilters } from '../../../store/search/slice';
import {
  DocumentCategory,
  DocumentType,
  FilingType,
  LetterType,
  PresentationType,
  TranscriptType,
} from '../../../store/document/types';
import DocumentCategoryUtils from '../../../components/FileExplorer/components/DocumentCategoryUtils';
import styles from './SignalFilters.module.scss';
import { trackEvent } from '../../../services/tracking';

type SelectOption = { value: string; label: string };

const buildCategoryOptions = (): SelectOption[] => {
  const categories = Object.values(DocumentCategory) as string[];
  return categories.map((c) => ({
    value: c,
    label: DocumentCategoryUtils.formatCategoryName(c as DocumentCategory),
  }));
};

const buildTypeOptionsForCategories = (categories: string[]): SelectOption[] => {
  const set = new Set<string>();
  categories.forEach((category) => {
    switch (category as DocumentCategory) {
      case DocumentCategory.FILING:
        Object.values(FilingType).forEach((v) => set.add(v));
        break;
      case DocumentCategory.TRANSCRIPT:
        Object.values(TranscriptType).forEach((v) => set.add(v));
        break;
      case DocumentCategory.LETTER:
        Object.values(LetterType).forEach((v) => set.add(v));
        break;
      case DocumentCategory.PRESENTATION:
        Object.values(PresentationType).forEach((v) => set.add(v));
        break;
      default:
        break;
    }
  });
  return Array.from(set).map((t) => ({
    value: t,
    label: DocumentCategoryUtils.formatDocumentTypeName(t as unknown as DocumentType),
  }));
};

interface SignalFiltersProps {
  isLoading: boolean;
  onSearch: () => void;
}

const SignalFilters: React.FC<SignalFiltersProps> = ({ isLoading, onSearch }) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((s) => s.signalSearch.filters);
  const categoryOptions = buildCategoryOptions();
  const typeOptions = buildTypeOptionsForCategories(filters.categories);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isLoading && filters.query.trim()) {
          onSearch();
        }
      }}
    >
      <TextInput
        value={filters.query}
        onChange={(v) => dispatch(setFilters({ query: v }))}
        onBlur={() => {
          if (filters.query.trim()) {
            trackEvent('SignalFilters Query Edited', { query: filters.query });
          }
        }}
        placeholder="Enter your search query..."
        disabled={isLoading}
        fullWidth
      />
      <TickerInput
        isMulti
        value={filters.selectedCompanies}
        onChange={(v) => {
          dispatch(setFilters({ selectedCompanies: v }));
          trackEvent('SignalFilters Company Selection Changed', {
            selectedCompanies: v.map((c) => c.ticker),
          });
        }}
        placeholder="Filter by companies"
        embeddedOnly
        noOptionsMessage="No companies found. This company can't be searched yet."
      />
      <MultiSelect
        label="Category"
        options={categoryOptions}
        value={filters.categories}
        onChange={(vals) => {
          dispatch(setFilters({ categories: vals }));
          if (!vals.length) dispatch(setFilters({ types: [] }));
          trackEvent('SignalFilters Categories Changed', { categories: vals });
        }}
        placeholder="All categories"
      />
      {filters.categories.length > 0 && (
        <MultiSelect
          label="Type"
          options={typeOptions}
          value={filters.types}
          onChange={(vals) => {
            dispatch(setFilters({ types: vals }));
            trackEvent('SignalFilters Types Changed', { types: vals });
          }}
          placeholder="All types"
        />
      )}
      <TextInput
        label="Calendar Year"
        value={filters.calendarYear || ''}
        onChange={(v) => dispatch(setFilters({ calendarYear: v }))}
        onBlur={() => {
          if ((filters.calendarYear || '').trim()) {
            trackEvent('SignalFilters CalendarYear Edited', {
              value: filters.calendarYear,
            });
          }
        }}
        placeholder="2023 or 2021-2023"
        disabled={isLoading}
        fullWidth
      />
      <Button type="submit" variant="primary" disabled={isLoading || !filters.query.trim()}>
        {isLoading ? 'Searching...' : 'Search'}
      </Button>
    </form>
  );
};

export default SignalFilters;
